export const UTM_STORAGE_KEY = 'utm_attribution'
export const UTM_ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000
export const UTM_VALUE_MAX_LENGTH = 256

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

export type UtmParamKey = (typeof UTM_PARAMS)[number]

export type UtmAttribution = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  captured_at: string
  landing_path: string
}

export type UtmAttributionPayload = Omit<UtmAttribution, 'captured_at' | 'landing_path'> & {
  captured_at?: string
  landing_path?: string
}

const sanitizeUtmValue = (value: string): string | undefined => {
  const trimmed = value
    .trim()
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0)
      return code > 31 && code !== 127
    })
    .join('')
  if (!trimmed) return undefined
  return trimmed.slice(0, UTM_VALUE_MAX_LENGTH)
}

export const parseUtmFromSearchParams = (search: string): Partial<Record<UtmParamKey, string>> => {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
  const result: Partial<Record<UtmParamKey, string>> = {}

  for (const key of UTM_PARAMS) {
    const raw = params.get(key)
    if (!raw) continue
    const sanitized = sanitizeUtmValue(raw)
    if (sanitized) {
      result[key] = sanitized
    }
  }

  return result
}

const hasAnyUtmParam = (utm: Partial<Record<UtmParamKey, string>>): boolean =>
  UTM_PARAMS.some((key) => Boolean(utm[key]))

const parseStoredAttribution = (raw: string): UtmAttribution | null => {
  try {
    const parsed = JSON.parse(raw) as UtmAttribution
    if (!parsed || typeof parsed !== 'object' || !parsed.captured_at) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const isAttributionWithinWindow = (attribution: UtmAttribution, now = Date.now()): boolean => {
  const capturedAt = Date.parse(attribution.captured_at)
  if (Number.isNaN(capturedAt)) return false
  return now - capturedAt <= UTM_ATTRIBUTION_WINDOW_MS
}

export const toUtmAttributionPayload = (
  attribution: UtmAttribution | null,
): UtmAttributionPayload | undefined => {
  if (!attribution) return undefined

  const payload: UtmAttributionPayload = {}
  for (const key of UTM_PARAMS) {
    const value = attribution[key]
    if (value) payload[key] = value
  }

  if (!hasAnyUtmParam(payload)) return undefined

  payload.captured_at = attribution.captured_at
  payload.landing_path = attribution.landing_path

  return payload
}

export const toUtmEventProperties = (
  attribution: UtmAttribution | null,
): Record<string, string> => {
  if (!attribution) return {}

  const properties: Record<string, string> = {}
  for (const key of UTM_PARAMS) {
    const value = attribution[key]
    if (value) properties[key] = value
  }
  return properties
}

export const captureUtmAttribution = (
  search: string,
  landingPath = typeof window !== 'undefined' ? window.location.pathname : '/',
): UtmAttribution | null => {
  const parsed = parseUtmFromSearchParams(search)
  if (!hasAnyUtmParam(parsed)) {
    return getStoredUtmAttribution()
  }

  const existing = getStoredUtmAttribution()
  if (existing) {
    return existing
  }

  const attribution: UtmAttribution = {
    ...parsed,
    captured_at: new Date().toISOString(),
    landing_path: landingPath,
  }

  localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(attribution))
  return attribution
}

export const getStoredUtmAttribution = (): UtmAttribution | null => {
  const raw = localStorage.getItem(UTM_STORAGE_KEY)
  if (!raw) return null

  const attribution = parseStoredAttribution(raw)
  if (!attribution) {
    localStorage.removeItem(UTM_STORAGE_KEY)
    return null
  }

  if (!isAttributionWithinWindow(attribution)) {
    localStorage.removeItem(UTM_STORAGE_KEY)
    return null
  }

  return attribution
}

export const clearStoredUtmAttribution = (): void => {
  localStorage.removeItem(UTM_STORAGE_KEY)
}
